
import React from 'react';
import {
    Box,
    Typography,
    Skeleton,
    Rating,
    CircularProgress,
    Button,
    Tooltip,
    IconButton
} from '@mui/material';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import paths from '../../../Infrastructure/Router/paths';
import { courseListingCardSx } from './styles';

const CourseListingCard = ({
    title,
    firstName,
    lastName,
    avatar,
    profileVector,
    courseImage,
    courseVector,
    profileUrl,
    imageError,
    setImageError,
    specialFont,
    imageLoaded,
    setImageLoaded,
    avatarLoaded,
    setAvatarLoaded,
    classSchedule,
    classesLoading,
    month,
    weekday,
    year,
    duration,
    cost,
    handleEnrolledCourse,
    firstClassScheduleKey,
    isCoursePage,
    countLoading,
    classCount,
    maxLearners,
    live,
    handleClick,
    SHOW_RATINGS,
    navigate,
    theme,
    dashboardState
}) => {
    return (
        <Box sx={{ ...courseListingCardSx.container, border: `3px solid ${theme.palette.primary.main}`, borderBottom: `3px solid ${theme.palette.primary.main}`, mt: { xs: dashboardState ? 1 : 0, md: dashboardState ? 1 : 0 } }}>
            <Box sx={{ ...courseListingCardSx.imageBox, borderBottom: `3px solid ${theme.palette.primary.main}` }}>
                {!imageLoaded && <Skeleton variant="rectangular" width="100%" height="100%" sx={courseListingCardSx.skeleton} />}
                <img
                    src={courseImage || courseVector}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = courseVector;
                        setImageError(true);
                    }}
                    onLoad={() => setImageLoaded(true)}
                    alt="Course"
                    style={{
                        objectFit: 'cover',
                        height: '100%',
                        width: '100%',
                        aspectRatio: "16 / 9",
                        display: imageLoaded ? 'block' : 'none'
                    }}
                />
            </Box>

            {/* Content Section */}
            <Box px={1} display="flex" flexDirection="column" gap={2} sx={courseListingCardSx.contentBox}>
                <Typography variant="h1" color="primary" sx={{ ...courseListingCardSx.titleText, color: theme.palette.primary.main }}>{title}</Typography>
                <Box sx={courseListingCardSx.educatorBox} onClick={isCoursePage ? () => navigate(paths.educators.path + `/${profileUrl}`) : null}>
                    {!avatarLoaded && <Skeleton variant="circular" width={50} height={50} />}
                    <img
                        src={avatar || profileVector}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = profileVector;
                        }}
                        onLoad={() => setAvatarLoaded(true)}
                        width="50px"
                        height="50px"
                        alt="Profile"
                        style={{
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            display: avatarLoaded ? 'block' : 'none'
                        }}
                    />
                    <Box>
                        <Typography sx={courseListingCardSx.educatorName}>By {firstName} {lastName}</Typography>
                        {SHOW_RATINGS && <Box sx={courseListingCardSx.ratingBox}><Rating sx={{ fontSize: '1.2rem' }} name="star-rating" value={5} precision={0.5} readOnly /></Box>}
                    </Box>
                </Box>

                {/* Schedule and Pricing */}
                {classesLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" my={3}><CircularProgress /></Box>
                ) : classSchedule && Object.keys(classSchedule).length > 0 ? (
                    <Box sx={courseListingCardSx.scheduleBox}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.primary.main }} mb={1}>
                                Starts on: <span style={{ fontWeight: '800' }}>{month}/{weekday}/{year}</span>
                            </Typography>
                            <Typography variant="h6" sx={courseListingCardSx.durationText}>
                                {/* eslint-disable-next-line eqeqeq */}
                                Duration: {duration} {duration == "1" ? "Week" : "Weeks"}
                            </Typography>
                            <Typography sx={courseListingCardSx.costText}>Cost: ${cost || 0}</Typography>
                        </Box>

                        {/* CTA Buttons */}
                        <Box sx={courseListingCardSx.ctaMainBox}>
                            {isCoursePage ? (
                                countLoading ? (
                                    <CircularProgress />
                                ) : classCount === maxLearners ? (
                                    <Typography sx={{ textAlign: 'center', fontSize: "22px", fontWeight: "bold" }}>Class Full</Typography>
                                ) : (
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <Box sx={courseListingCardSx.ctaBox}>
                                            <Button variant="contained" color="primary" sx={{ '&:hover': { cursor: live ? 'pointer' : 'not-allowed' }, pointerEvents: live ? 'auto' : 'none', textTransform: 'uppercase' }} onClick={() => handleClick(firstClassScheduleKey)}>See Schedules</Button>
                                            <Button variant="contained" color="primary" sx={{ backgroundColor: '#fff', color: 'primary.main', borderColor: 'primary.main', '&:hover': { backgroundColor: '#f5f5f5', cursor: live ? 'pointer' : 'not-allowed' }, pointerEvents: live ? 'auto' : 'none' }} onClick={() => handleEnrolledCourse(firstClassScheduleKey)}>Enroll Now</Button>
                                        </Box>
                                        <Typography sx={courseListingCardSx.infoText}>
                                            <Tooltip enterTouchDelay={0} leaveTouchDelay={3000} title="If you decide the course isn't right for you, it can be canceled anytime for a full refund up to and including the first day of class.">
                                                <IconButton sx={{ p: 0, mr: 0.5 }}><IoIosInformationCircleOutline /></IconButton>
                                            </Tooltip>
                                            <span style={{ cursor: 'pointer' }} onClick={() => isCoursePage ? navigate(paths.cancellationPage.path) : null}>First Class Money Back Guarantee</span>
                                        </Typography>
                                    </Box>
                                )
                            ) : (
                                <Box display={'flex'} flexDirection="column" alignItems="center">
                                    <Box sx={courseListingCardSx.ctaBox}>
                                        <Button variant="contained" color="primary" sx={{ textTransform: 'uppercase' }} onClick={() => handleClick(firstClassScheduleKey)}>See Schedules</Button>
                                        <Button variant="contained" color="primary" sx={{ backgroundColor: '#fff', color: 'primary.main', borderColor: 'primary.main', '&:hover': { backgroundColor: '#f5f5f5', cursor: live ? 'pointer' : 'not-allowed' }, pointerEvents: live ? 'auto' : 'none' }} onClick={() => handleEnrolledCourse(firstClassScheduleKey)}>Enroll Now</Button>
                                    </Box>
                                    <Typography sx={courseListingCardSx.infoText}>
                                        <Tooltip enterTouchDelay={0} leaveTouchDelay={3000} title="If you decide the course isn't right for you, it can be canceled anytime for a full refund up to and including the first day of class.">
                                            <IconButton sx={{ p: 0, mr: 0.5 }}><IoIosInformationCircleOutline /></IconButton>
                                        </Tooltip>
                                        <span style={{ '&:hover': { cursor: live ? 'pointer' : 'not-allowed' } }} onClick={() => isCoursePage ? navigate(paths.cancellationPage.path) : null}>First Class Money Back Guarantee</span>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Typography sx={courseListingCardSx.costText}>Cost: ${cost || 0}</Typography>
                        <Typography sx={{ textAlign: "center" }}>No schedules available</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CourseListingCard;
